/**
 * Passdown Modal Component
 *
 * Modal for creating and sending shift passdown reports.
 * Features:
 * - Department and shift selection
 * - Live preview of alerts and tasks
 * - Optional supervisor notes
 * - Include completed tasks toggle
 * - Send to configured recipients or custom emails
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  passdownAPI,
  departmentAPI,
  shiftAPI,
  userAPI,
  companyAPI,
  PassdownPreview,
  PassdownPreviewAlert,
  PassdownCreate,
  PassdownSendResult,
  PassdownReason,
  PassdownDeliveryMode,
} from '../services/api';
import { Department, Shift } from '../types/operations';
import { useAuth } from '../contexts/AuthContext';

interface User {
  id: number;
  email: string;
  full_name: string;
  is_active: boolean;
}

const REASON_LABELS: Record<PassdownReason, string> = {
  shift_end: 'Shift End',
  break: 'Break',
  emergency: 'Emergency',
  reassignment: 'Reassignment',
  other: 'Other',
};

const SEVERITY_STYLES: Record<string, { bg: string; text: string }> = {
  critical: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300' },
  warning: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-300' },
  info: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300' },
  high: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300' },
  medium: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-300' },
  low: { bg: 'bg-gray-100 dark:bg-gray-700/30', text: 'text-gray-600 dark:text-gray-400' },
};

/** Format an ISO date (YYYY-MM-DD) as MM/DD/YYYY. */
const formatDate = (dateStr: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr || '');
  return match ? `${match[2]}/${match[3]}/${match[1]}` : dateStr || '';
};

/** Turn a snake_case identifier into readable Title Case words. */
const humanizeType = (value: string): string =>
  (value || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/** Format a KPI value with a unit inferred from the KPI name. */
const formatMetric = (value: number | null | undefined, kpiName: string): string => {
  if (value === null || value === undefined) return '—';
  const name = (kpiName || '').toLowerCase();
  if (name.includes('downtime')) return `${Math.round(value)} min`;
  if (name.includes('scrap') || name.includes('efficiency') || name.includes('utilization')) {
    return `${value.toFixed(1)}%`;
  }
  return `${value}`;
};

/** Human-readable alert status: Unacknowledged / Acknowledged / Resolved. */
const alertStatusLabel = (alert: PassdownPreviewAlert): string => {
  if (alert.status === 'resolved') return 'Resolved';
  if (alert.status === 'acknowledged') {
    if (alert.action_needed === true || alert.task_count > 0) {
      return 'Acknowledged · Task Created';
    }
    return 'Acknowledged';
  }
  return 'Unacknowledged';
};

interface PassdownModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  defaultShiftName?: string;
}

const PassdownModal: React.FC<PassdownModalProps> = ({ isOpen, onClose, onSuccess, defaultShiftName }) => {
  const { vellrixUser } = useAuth();
  // Form state
  const [departmentId, setDepartmentId] = useState<number | null>(null);
  const [shiftName, setShiftName] = useState('');
  const [shiftDate, setShiftDate] = useState(new Date().toISOString().split('T')[0]);
  const [includeCompletedTasks, setIncludeCompletedTasks] = useState(false);
  const [supervisorNotes, setSupervisorNotes] = useState('');
  const [customEmails, setCustomEmails] = useState('');

  // Accountability fields
  const [assignedToUserId, setAssignedToUserId] = useState<number | null>(null);
  const [reason, setReason] = useState<PassdownReason>('shift_end');
  const [summary, setSummary] = useState('');
  const [tasksCompleted, setTasksCompleted] = useState('');
  const [nextActions, setNextActions] = useState('');
  const [slaDeadline, setSlaDeadline] = useState('');

  // Data state
  const [departments, setDepartments] = useState<Department[]>([]);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [preview, setPreview] = useState<PassdownPreview | null>(null);
  const [deliveryMode, setDeliveryMode] = useState<PassdownDeliveryMode>('email_only');

  // UI state
  const [step, setStep] = useState<'configure' | 'preview' | 'sending' | 'sent'>('configure');
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sendResult, setSendResult] = useState<PassdownSendResult | null>(null);

  // Fetch departments, users, and settings on mount
  useEffect(() => {
    if (!isOpen) return;

    // Only show departments the user belongs to
    const userDeptIds = vellrixUser?.departments?.map(d => d.id) || [];
    if (userDeptIds.length > 0) {
      departmentAPI.list().then((allDepts) => {
        const userDepts = allDepts.filter(d => userDeptIds.includes(d.id));
        setDepartments(userDepts);
        // Default to user's first department
        if (userDepts.length > 0) {
          setDepartmentId(userDepts[0].id);
        }
      }).catch((err) => console.error('Failed to fetch departments:', err));
    } else {
      // Fallback: if user has no departments assigned, show all (admin case)
      departmentAPI.list().then((depts) => {
        setDepartments(depts);
        if (depts.length > 0) {
          setDepartmentId(depts[0].id);
        }
      }).catch((err) => console.error('Failed to fetch departments:', err));
    }

    userAPI.list().then((userList) => {
      setUsers(userList.filter(u => u.is_active));
    }).catch((err) => console.error('Failed to fetch users:', err));

    companyAPI.getSettings().then((settings) => {
      setDeliveryMode(settings.passdown_delivery_mode);
    }).catch((err) => console.error('Failed to fetch company settings:', err));
  }, [isOpen]);

  // Fetch shifts when department changes
  useEffect(() => {
    if (!departmentId) {
      setShifts([]);
      setShiftName('');
      return;
    }
    shiftAPI.list(departmentId).then((shiftsData) => {
      setShifts(shiftsData);
      // Default to the shift selected on the dashboard, otherwise first
      if (defaultShiftName && shiftsData.find(s => s.name === defaultShiftName)) {
        setShiftName(defaultShiftName);
      } else {
        setShiftName(shiftsData.length > 0 ? shiftsData[0].name : '');
      }
    }).catch((err) => console.error('Failed to fetch shifts:', err));
  }, [departmentId, defaultShiftName]);

  // Fetch preview when config changes
  const fetchPreview = useCallback(async () => {
    if (!departmentId || !shiftDate || !shiftName) return;

    try {
      setPreviewLoading(true);
      setError(null);
      const previewData = await passdownAPI.preview(
        departmentId,
        shiftDate,
        shiftName,
        includeCompletedTasks
      );
      setPreview(previewData);
    } catch (err: any) {
      console.error('Failed to fetch preview:', err);
      setError(err.response?.data?.detail || 'Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  }, [departmentId, shiftDate, shiftName, includeCompletedTasks]);

  useEffect(() => {
    if (step === 'preview') {
      fetchPreview();
    }
  }, [step, fetchPreview]);

  const handleNextStep = () => {
    if (step === 'configure' && departmentId && shiftDate && shiftName) {
      setStep('preview');
    }
  };

  const handleBack = () => {
    if (step === 'preview') {
      setStep('configure');
    }
  };

  const handleSend = async () => {
    if (!departmentId || !shiftDate || !shiftName) return;

    // Validate based on delivery mode
    const showAssignment = deliveryMode === 'assignment_only' || deliveryMode === 'email_and_assignment';
    const showEmail = deliveryMode === 'email_only' || deliveryMode === 'email_and_assignment';

    // For assignment_only mode, require an assigned user
    if (deliveryMode === 'assignment_only' && !assignedToUserId) {
      setError('Please assign this passdown to a user');
      return;
    }

    try {
      setStep('sending');
      setLoading(true);
      setError(null);

      // Create the passdown with fields based on delivery mode
      const passdownData: PassdownCreate = {
        department_id: departmentId,
        shift_date: shiftDate,
        shift_name: shiftName,
        include_completed_tasks: includeCompletedTasks,
        supervisor_notes: supervisorNotes || undefined,
        // Accountability fields (only if assignment is enabled)
        ...(showAssignment && {
          assigned_to_user_id: assignedToUserId || undefined,
          reason: reason,
          summary: summary || undefined,
          tasks_completed: tasksCompleted || undefined,
          next_actions: nextActions || undefined,
          sla_deadline: slaDeadline || undefined,
        }),
      };

      const passdown = await passdownAPI.create(passdownData);

      // Send email only if email mode is enabled
      let result: PassdownSendResult;
      if (showEmail) {
        const customEmailList = customEmails
          .split(/[,;\n]/)
          .map(e => e.trim())
          .filter(e => e.includes('@'));

        result = await passdownAPI.send(
          passdown.id,
          customEmailList.length > 0 ? { recipient_emails: customEmailList } : undefined
        );
      } else {
        // Assignment only - no email sent
        result = {
          success: true,
          error: null,
          recipients_count: 0,
          message: 'Passdown created and assigned successfully (no email sent)',
        };
      }

      setSendResult(result);
      setStep('sent');

      if (result.success && onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      console.error('Failed to send passdown:', err);
      setError(err.response?.data?.detail || 'Failed to send passdown');
      setStep('preview');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset state
    setStep('configure');
    setPreview(null);
    setSupervisorNotes('');
    setCustomEmails('');
    setSendResult(null);
    setError(null);
    // Reset accountability fields
    setAssignedToUserId(null);
    setReason('shift_end');
    setSummary('');
    setTasksCompleted('');
    setNextActions('');
    setSlaDeadline('');
    onClose();
  };

  if (!isOpen) return null;

  // Determine which sections to show based on delivery mode
  const showAssignment = deliveryMode === 'assignment_only' || deliveryMode === 'email_and_assignment';
  const showEmail = deliveryMode === 'email_only' || deliveryMode === 'email_and_assignment';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Create Shift Passdown
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          {/* Step: Configure */}
          {step === 'configure' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Department */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Department *
                  </label>
                  <select
                    value={departmentId || ''}
                    onChange={(e) => setDepartmentId(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select department...</option>
                    {departments.map((dept) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Shift */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Shift *
                  </label>
                  <select
                    value={shiftName}
                    onChange={(e) => setShiftName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select shift...</option>
                    {shifts.map((shift) => (
                      <option key={shift.id} value={shift.name}>
                        {shift.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Date *
                  </label>
                  <input
                    type="date"
                    value={shiftDate}
                    onChange={(e) => setShiftDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Options */}
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeCompletedTasks}
                    onChange={(e) => setIncludeCompletedTasks(e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Include completed tasks from today
                  </span>
                </label>
              </div>

              {/* Accountability Section - shown when assignment mode is enabled */}
              {showAssignment && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
                    Passdown Assignment {deliveryMode === 'assignment_only' ? '*' : '(Optional)'}
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    {/* Assign To */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Assign To {deliveryMode === 'assignment_only' ? '*' : ''}
                      </label>
                      <select
                        value={assignedToUserId || ''}
                        onChange={(e) => setAssignedToUserId(e.target.value ? Number(e.target.value) : null)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">{deliveryMode === 'assignment_only' ? 'Select user...' : 'No assignment (email only)'}</option>
                        {users.map((user) => (
                          <option key={user.id} value={user.id}>
                            {user.full_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Reason */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Reason
                      </label>
                      <select
                        value={reason}
                        onChange={(e) => setReason(e.target.value as PassdownReason)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                      >
                        {(Object.keys(REASON_LABELS) as PassdownReason[]).map((key) => (
                          <option key={key} value={key}>
                            {REASON_LABELS[key]}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* SLA Deadline */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Acknowledgment Deadline
                      </label>
                      <input
                        type="datetime-local"
                        value={slaDeadline}
                        onChange={(e) => setSlaDeadline(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        When the passdown becomes overdue
                      </p>
                    </div>
                  </div>

                  {/* Summary */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Summary
                    </label>
                    <textarea
                      value={summary}
                      onChange={(e) => setSummary(e.target.value)}
                      rows={2}
                      placeholder="Brief summary of shift events..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 resize-none"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Tasks Completed */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        What Was Done This Shift
                      </label>
                      <textarea
                        value={tasksCompleted}
                        onChange={(e) => setTasksCompleted(e.target.value)}
                        rows={3}
                        placeholder="List key accomplishments..."
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 resize-none"
                      />
                    </div>

                    {/* Next Actions */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        What Needs To Happen Next
                      </label>
                      <textarea
                        value={nextActions}
                        onChange={(e) => setNextActions(e.target.value)}
                        rows={3}
                        placeholder="List next steps for incoming shift..."
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 resize-none"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Supervisor Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Supervisor Notes (optional)
                </label>
                <textarea
                  value={supervisorNotes}
                  onChange={(e) => setSupervisorNotes(e.target.value)}
                  rows={3}
                  placeholder="Add any notes for the next shift..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>

              {/* Custom Recipients - shown when email mode is enabled */}
              {showEmail && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Additional Recipients (optional)
                  </label>
                  <textarea
                    value={customEmails}
                    onChange={(e) => setCustomEmails(e.target.value)}
                    rows={2}
                    placeholder="Enter email addresses separated by commas or new lines..."
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 resize-none"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Leave empty to send to configured report recipients only
                  </p>
                </div>
              )}

              {/* Delivery Mode Indicator */}
              <div className="text-xs text-gray-500 dark:text-gray-400 p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <span className="font-medium">Delivery Mode:</span>{' '}
                {deliveryMode === 'email_only' && 'Email Only - Passdowns will be sent via email to recipients'}
                {deliveryMode === 'assignment_only' && 'Assignment Only - Passdowns will be assigned to users for acknowledgment (no email)'}
                {deliveryMode === 'email_and_assignment' && 'Email + Assignment - Passdowns can be emailed and/or assigned to users'}
              </div>
            </div>
          )}

          {/* Step: Preview */}
          {step === 'preview' && (
            <div className="space-y-6">
              {previewLoading ? (
                <div className="text-center py-12">
                  <div className="inline-block w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  <p className="mt-2 text-gray-500 dark:text-gray-400">Loading preview...</p>
                </div>
              ) : preview ? (
                <>
                  {/* Header Info */}
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {preview.department_name}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {preview.shift_name} - {formatDate(preview.shift_date)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Alerts Summary */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Alerts ({preview.alerts_total})
                    </h4>
                    <div className="flex gap-3 mb-3">
                      <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs rounded">
                        {preview.alerts_critical} Critical
                      </span>
                      <span className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 text-xs rounded">
                        {preview.alerts_warning} Warning
                      </span>
                      <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs rounded">
                        {preview.alerts_acknowledged} Acknowledged
                      </span>
                    </div>
                    {preview.alerts.length > 0 ? (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {preview.alerts.slice(0, 5).map((alert) => (
                          <div
                            key={alert.id}
                            className={`p-2 rounded border-l-4 ${
                              SEVERITY_STYLES[alert.severity]?.bg || 'bg-gray-50'
                            } ${
                              alert.severity === 'critical'
                                ? 'border-red-500'
                                : alert.severity === 'warning'
                                ? 'border-yellow-500'
                                : 'border-blue-500'
                            }`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                {humanizeType(alert.alert_type)}
                              </span>
                              <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-white/70 dark:bg-black/30 text-gray-700 dark:text-gray-200">
                                {alertStatusLabel(alert)}
                              </span>
                            </div>
                            <div className="text-sm text-gray-700 dark:text-gray-300">
                              {alert.machine_name || alert.machine_identifier || 'Department-wide'}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              Actual: {formatMetric(alert.kpi_value, alert.kpi_name)}
                              {' · '}
                              Target: {formatMetric(alert.threshold_value, alert.kpi_name)}
                            </div>
                          </div>
                        ))}
                        {preview.alerts.length > 5 && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                            +{preview.alerts.length - 5} more alerts
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                        No alerts for this shift.
                      </p>
                    )}
                  </div>

                  {/* Shift Performance - totals + per-machine KPIs */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Shift Performance
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-center">
                        <div className="text-xl font-bold text-gray-900 dark:text-white">
                          {preview.kpi_summary.total_good_qty.toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Good Output</div>
                      </div>
                      <div className={`rounded-lg p-3 text-center ${
                        preview.kpi_summary.scrap_percentage >= 5
                          ? 'bg-red-50 dark:bg-red-900/20'
                          : preview.kpi_summary.scrap_percentage >= 3
                          ? 'bg-yellow-50 dark:bg-yellow-900/20'
                          : 'bg-gray-50 dark:bg-gray-700/50'
                      }`}>
                        <div className={`text-xl font-bold ${
                          preview.kpi_summary.scrap_percentage >= 5
                            ? 'text-red-600 dark:text-red-400'
                            : preview.kpi_summary.scrap_percentage >= 3
                            ? 'text-yellow-600 dark:text-yellow-400'
                            : 'text-gray-900 dark:text-white'
                        }`}>
                          {preview.kpi_summary.scrap_percentage.toFixed(1)}%
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Scrap Rate</div>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-center">
                        <div className="text-xl font-bold text-gray-900 dark:text-white">
                          {preview.kpi_summary.efficiency_percentage !== null
                            ? `${preview.kpi_summary.efficiency_percentage.toFixed(1)}%`
                            : '—'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Efficiency</div>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-center">
                        <div className="text-xl font-bold text-gray-900 dark:text-white">
                          {Math.round(preview.kpi_summary.total_downtime_min)}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Downtime (min)</div>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-center">
                        <div className="text-xl font-bold text-gray-900 dark:text-white">
                          {preview.kpi_summary.machines_reporting}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Machines</div>
                      </div>
                    </div>
                    {preview.machine_kpis.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-xs uppercase text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                              <th className="py-2 text-left">Machine</th>
                              <th className="py-2 text-right">Good</th>
                              <th className="py-2 text-right">Scrap</th>
                              <th className="py-2 text-right">Scrap %</th>
                              <th className="py-2 text-right">Downtime</th>
                              <th className="py-2 text-right">Efficiency</th>
                            </tr>
                          </thead>
                          <tbody>
                            {preview.machine_kpis.map((m) => (
                              <tr
                                key={m.machine_id}
                                className="border-b border-gray-100 dark:border-gray-700/50"
                              >
                                <td className="py-2 text-gray-900 dark:text-white">
                                  {m.machine_name || m.machine_identifier}
                                </td>
                                <td className="py-2 text-right text-gray-700 dark:text-gray-300">
                                  {m.good_qty.toLocaleString()}
                                </td>
                                <td className="py-2 text-right text-gray-700 dark:text-gray-300">
                                  {m.scrap_qty.toLocaleString()}
                                </td>
                                <td className="py-2 text-right text-gray-700 dark:text-gray-300">
                                  {m.scrap_percentage.toFixed(1)}%
                                </td>
                                <td className="py-2 text-right text-gray-700 dark:text-gray-300">
                                  {Math.round(m.downtime_min)} min
                                </td>
                                <td className="py-2 text-right text-gray-700 dark:text-gray-300">
                                  {m.efficiency_percentage !== null
                                    ? `${m.efficiency_percentage.toFixed(1)}%`
                                    : '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                        No machine data for this shift.
                      </p>
                    )}
                  </div>

                  {/* Tasks Summary */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Open Tasks ({preview.tasks_open})
                    </h4>
                    {Object.keys(preview.tasks_by_category).length > 0 ? (
                      <div className="space-y-4 max-h-48 overflow-y-auto">
                        {Object.entries(preview.tasks_by_category).map(([category, tasks]) => (
                          <div key={category}>
                            <h5 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase mb-2">
                              {category} ({tasks.length})
                            </h5>
                            <div className="space-y-1">
                              {tasks.slice(0, 3).map((task) => (
                                <div
                                  key={task.id}
                                  className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded"
                                >
                                  <span className="text-sm text-gray-900 dark:text-white truncate">
                                    {task.title}
                                  </span>
                                  <span
                                    className={`text-xs px-2 py-0.5 rounded ${
                                      SEVERITY_STYLES[task.severity]?.bg || ''
                                    } ${SEVERITY_STYLES[task.severity]?.text || ''}`}
                                  >
                                    {task.severity}
                                  </span>
                                </div>
                              ))}
                              {tasks.length > 3 && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                                  +{tasks.length - 3} more
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                        No open tasks.
                      </p>
                    )}
                  </div>

                  {/* Completed Tasks (if included) */}
                  {includeCompletedTasks && preview.completed_tasks.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        Completed Today ({preview.tasks_completed_today})
                      </h4>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {preview.completed_tasks.slice(0, 5).map((task) => (
                          <div
                            key={task.id}
                            className="p-2 bg-green-50 dark:bg-green-900/20 rounded text-sm"
                          >
                            <span className="text-green-700 dark:text-green-300">
                              {task.title}
                            </span>
                            {task.category_name && (
                              <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                                ({task.category_name})
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Supervisor Notes Preview */}
                  {supervisorNotes && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Supervisor Notes
                      </h4>
                      <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg text-sm text-gray-700 dark:text-gray-300 italic">
                        {supervisorNotes}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                  No preview available
                </p>
              )}
            </div>
          )}

          {/* Step: Sending */}
          {step === 'sending' && (
            <div className="text-center py-12">
              <div className="inline-block w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              <p className="mt-4 text-lg text-gray-700 dark:text-gray-300">
                Sending passdown report...
              </p>
            </div>
          )}

          {/* Step: Sent */}
          {step === 'sent' && sendResult && (
            <div className="text-center py-8">
              {sendResult.success ? (
                <>
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full mb-4">
                    <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    Passdown Sent Successfully!
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 mb-4">
                    {sendResult.recipients_count > 0
                      ? `Sent to ${sendResult.recipients_count} recipient${sendResult.recipients_count > 1 ? 's' : ''}`
                      : sendResult.message || 'No recipients configured'}
                  </p>
                  {sendResult.sent_to && sendResult.sent_to.length > 0 && (
                    <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
                      <p className="font-medium mb-1">Sent to:</p>
                      <ul className="list-disc list-inside">
                        {sendResult.sent_to.map((email) => (
                          <li key={email}>{email}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
                    <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    Failed to Send
                  </h3>
                  <p className="text-red-600 dark:text-red-400">
                    {sendResult.error || 'Unknown error occurred'}
                  </p>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            {step === 'preview' && (
              <button
                onClick={handleBack}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Back
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              {step === 'sent' ? 'Close' : 'Cancel'}
            </button>

            {step === 'configure' && (
              <button
                onClick={handleNextStep}
                disabled={!departmentId || !shiftDate || !shiftName}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Preview
              </button>
            )}

            {step === 'preview' && (
              <button
                onClick={handleSend}
                disabled={loading || previewLoading}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Send Passdown
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PassdownModal;
